/* eslint-disable no-unused-vars */
import { Language, Region, ShoeSizeSystem, UnitSystem } from '../frontend/enums/languageEnums';
import { AllowedPrinterTypes } from '../frontend/enums/printerEnums';

interface MarketingInformation {
    heardFrom: 'appStore' | 'google' | 'youtube' | 'social' | 'friends' | 'other' | null;
    heardFromAdditionalInfo?: string;
}

interface SettingsType {
    agbAccepted?: boolean;
    newsletterAccepted?: boolean;
    preferredLanguage?: Language;
    preferredUnitSystem?: UnitSystem;
    preferredShoeSizeSystem?: ShoeSizeSystem;
    /** Merchant's primary sales market, chosen once during onboarding. */
    primarySalesRegion?: Region;
    email: string;
    name?: string;
    defaultPayoutMethod?: string;
    paypalPayoutMethod: boolean;
    stripePayoutMethod: boolean;
    payoutLast4?: string;
    paypalEmailMasked?: string;
    ownAffiliationPartnerId: string;
    payoutAddress?: AddressType;
    vatId?: string;
    shopDomains: string[];
    disconnectedShopDomains?: string[];
    shops: { [key: string]: ShopSettings };
    hideDtfWarning?: boolean;
    isBetaTester?: boolean;
    lastUsedStore?: string;
    lastUsedCollections?: Record<string, string[]>;
    lastUsedCatalogs?: Record<string, string[]>;
    lastUsedShippingProfiles?: Record<string, string>;
    marketingInformation?: MarketingInformation;
}

interface ShopSettings {
    _id?: string;
    shop_address: string;
    shop_default_payment_method?: string;
    shop_default_payment_method_usa?: string;
    shop_domain: string;
    shop_email?: string;
    shop_name?: string;
    shop_notification_payment_failed: boolean;
    shop_notification_personalized_design: boolean;
    shop_phone?: string;
    shopPaymentInformations?: {
        [key in Region]: { stripeConfigured: boolean; stripeLast4?: string; paypalConfigured: boolean; walletConfigured?: boolean };
    };
    is_vip?: boolean;
    onboardingFlowCompleted?: boolean;
    isInstalled: boolean;
    disconnectedAt?: Date;
    productLanguage?: Language;
    productUnitSystem?: UnitSystem;
    productShoeSizeSystem?: ShoeSizeSystem;
    preferredPrinterTypeTeeInBlue?: AllowedPrinterTypes;
    apiToken?: string;
    integrationApiInfos: {
        [key: string]: {
            //key is one of Object.values(INTEGRATION_API_PARTNER)
            webhookUrl: string;
        };
    };
    autoAvailabilityUpdate?: boolean;
    taxExempt?: boolean;
    vatId?: string;
    shop_owner?: string;
    invoiceAddress?: AddressType;
    returnAddresses?: { [key in Region]?: AddressType };
    shopIntegrationType: EShopBrand;
    accessScopesGranted?: boolean;
    tiktokShop?: {
        region?: string; // z.B. 'DE', 'FR', 'US'
        responsiblePersonId?: string;
    };
    subscriptions?: {
        returnManagement?: {
            isActive: boolean;
            subscriptionId: string;
        };
    };
}

interface AddressType {
    address1: string;
    address1_street: string;
    address1_number: string;
    city: string;
    country_name: string;
    name?: string;
    shop_owner: string;
    zip: string;
    countryCode: InvoiceAddressCountries;
    province_code?: string;
}

enum INTEGRATION_API_PARTNER {
    TEE_IN_BLUE = 'teeinblue',
    ETSY = 'etsy',
}

enum InvoiceAddressCountries {
    AE = 'AE',
    AT = 'AT',
    BE = 'BE',
    BG = 'BG',
    CH = 'CH',
    CY = 'CY',
    CZ = 'CZ',
    DE = 'DE',
    DK = 'DK',
    EE = 'EE',
    ES = 'ES',
    FI = 'FI',
    FR = 'FR',
    GB = 'GB',
    GR = 'GR',
    HR = 'HR',
    HU = 'HU',
    IE = 'IE',
    IT = 'IT',
    LT = 'LT',
    LU = 'LU',
    LV = 'LV',
    MT = 'MT',
    NO = 'NO',
    NL = 'NL',
    PL = 'PL',
    PT = 'PT',
    RO = 'RO',
    SE = 'SE',
    SI = 'SI',
    SK = 'SK',
    US = 'US',
}

enum EShopBrand {
    SHOPIFY = 'shopify',
    ETSY = 'etsy',
    WOOCOMMERCE = 'woocommerce',
    AMAZON = 'amazon',
    TIKTOK = 'tiktok',
}

export { SettingsType, ShopSettings, AddressType, INTEGRATION_API_PARTNER, InvoiceAddressCountries, EShopBrand };
